import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useProperty } from "@/lib/property";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PlusIcon, BuildingsIcon, UploadSimpleIcon, PencilSimpleIcon, TrashIcon,
  ArrowSquareOutIcon, PhoneIcon, EnvelopeSimpleIcon, QrCodeIcon, CopyIcon,
} from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";

const EMPTY = { name: "", address: "", phone: "", email: "", cover_image: "", check_in_time: "14:00", check_out_time: "11:00" };

function PropertyForm({ initial, onSubmit, submitLabel, testIdSubmit }) {
  const [form, setForm] = useState(initial);
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="property-name" /></div>
      <div><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required data-testid="property-address" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="property-phone" /></div>
        <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="property-email" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Check-in time</Label>
          <Input type="time" value={form.check_in_time || "14:00"} onChange={(e) => setForm({ ...form, check_in_time: e.target.value })} data-testid="property-checkin-time" />
        </div>
        <div>
          <Label>Check-out time</Label>
          <Input type="time" value={form.check_out_time || "11:00"} onChange={(e) => setForm({ ...form, check_out_time: e.target.value })} data-testid="property-checkout-time" />
          <p className="text-[10px] text-muted-foreground mt-1">Drives the "checkout in Xh Ym" countdown on the Rooms page.</p>
        </div>
      </div>
      <div><Label>Cover image URL <span className="text-muted-foreground text-xs">(or upload from card)</span></Label><Input value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></div>
      <DialogFooter><Button type="submit" data-testid={testIdSubmit}>{submitLabel}</Button></DialogFooter>
    </form>
  );
}

export default function Properties() {
  const { user } = useAuth();
  const { refresh, setActive } = useProperty();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrProperty, setQrProperty] = useState(null);
  const [masterInfo, setMasterInfo] = useState({ is_master: false, master_email: "theelocalshelf@gmail.com" });

  const load = () => api.get("/properties").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/platform/master").then((r) => setMasterInfo(r.data)).catch(() => {});
  }, []);

  const create = async (form) => {
    try {
      await api.post("/properties", form);
      toast.success("Property added");
      setOpen(false);
      load();
      refresh();
    } catch { toast.error("Failed to add property"); }
  };

  const saveEdit = async (form) => {
    try {
      const { name, address, phone, email, cover_image, check_in_time, check_out_time } = form;
      await api.patch(`/properties/${editing.id}`, { name, address, phone, email, cover_image, check_in_time, check_out_time });
      toast.success("Property updated");
      setEditing(null);
      load();
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?\n\nThis will also delete all rooms for this property. This cannot be undone.`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Property removed");
      load();
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openDashboard = (p) => {
    setActive(p.id);
    navigate("/dashboard");
  };

  const canManage = user?.role === "admin" || user?.role === "manager";
  const isMaster = !!masterInfo.is_master;
  const canDelete = isMaster;
  const canCreate = isMaster;

  return (
    <div>
      <PageHeader
        overline="Portfolio"
        title="Properties"
        description="Every hotel your team operates. Click any card to jump straight to its dashboard."
        actions={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="property-new-btn"><PlusIcon size={16} className="mr-1" /> Add property</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New property</DialogTitle></DialogHeader>
              <PropertyForm initial={EMPTY} onSubmit={create} submitLabel="Create" testIdSubmit="property-submit-btn" />
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((p) => (
          <PropertyCard
            key={p.id}
            property={p}
            canManage={canManage}
            canDelete={canDelete}
            onEdit={() => setEditing(p)}
            onDelete={() => remove(p)}
            onOpenDashboard={() => openDashboard(p)}
            onShowQr={() => setQrProperty(p)}
            onChanged={load}
          />
        ))}
      </div>

      {!canCreate && canManage && (
        <p className="text-xs text-muted-foreground mt-6 max-w-2xl">
          <strong>Note:</strong> Adding new properties or deleting existing ones is restricted to the master account
          (<code className="font-mono-plex">{masterInfo.master_email}</code>). Contact the owner to onboard a new hotel.
        </p>
      )}

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit {editing.name}</DialogTitle></DialogHeader>
            <PropertyForm
              initial={{
                name: editing.name,
                address: editing.address,
                phone: editing.phone || "",
                email: editing.email || "",
                cover_image: editing.cover_image || "",
                check_in_time: editing.check_in_time || "14:00",
                check_out_time: editing.check_out_time || "11:00",
              }}
              onSubmit={saveEdit}
              submitLabel="Save changes"
              testIdSubmit="property-edit-save-btn"
            />
          </DialogContent>
        </Dialog>
      )}

      {qrProperty && (
        <Dialog open onOpenChange={(v) => !v && setQrProperty(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Public booking · {qrProperty.name}</DialogTitle></DialogHeader>
            <BookingQrPanel property={qrProperty} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function BookingQrPanel({ property }) {
  const bookingUrl = `${window.location.origin}/book/${property.id}`;
  const copy = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success("Link copied");
  };
  const downloadQr = () => {
    const canvas = document.getElementById(`qr-${property.id}`);
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${property.name.replace(/\s+/g, "-").toLowerCase()}-booking-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Print or share this QR / link. Travellers can scan it to book <strong>{property.name}</strong> directly from their phone —
        no staff involvement needed.
      </p>
      <div className="flex justify-center bg-white p-6 rounded-lg border border-border">
        <QRCodeCanvas
          id={`qr-${property.id}`}
          value={bookingUrl}
          size={220}
          level="H"
          includeMargin
          data-testid={`property-qr-canvas-${property.id}`}
        />
      </div>
      <div className="rounded-md bg-muted/40 border border-border p-3">
        <p className="label-overline">Direct booking link</p>
        <div className="flex items-center gap-2 mt-1.5">
          <code className="font-mono-plex text-xs break-all flex-1" data-testid={`property-qr-url-${property.id}`}>{bookingUrl}</code>
          <Button size="icon" variant="outline" onClick={copy} data-testid={`property-qr-copy-${property.id}`}><CopyIcon size={14} /></Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={downloadQr} className="flex-1" data-testid={`property-qr-download-${property.id}`}>
          Download QR (PNG)
        </Button>
        <Button variant="outline" onClick={() => window.open(bookingUrl, "_blank")} className="flex-1">
          Preview page
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground italic">
        Bookings made via this link show up in Timeline &amp; Reservations tagged as "Booking widget".
      </p>
    </div>
  );
}

function PropertyCard({ property: p, canManage, canDelete, onEdit, onDelete, onOpenDashboard, onShowQr, onChanged }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/properties/${p.id}/cover`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <Card className="overflow-hidden group/card" data-testid={`property-card-${p.id}`}>
      <div className="aspect-[16/9] bg-muted relative">
        {p.cover_image && <img src={p.cover_image} alt={p.name} className="h-full w-full object-cover" />}
        {!p.cover_image && (
          <div className="h-full w-full flex items-center justify-center">
            <BuildingsIcon size={40} weight="duotone" className="text-muted-foreground/40" />
          </div>
        )}
        {canManage && (
          <>
            <input ref={fileRef} type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={upload} data-testid={`property-cover-${p.id}`} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="absolute bottom-3 right-3 bg-card/95 border border-border rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity"
              data-testid={`property-cover-btn-${p.id}`}
            >
              <UploadSimpleIcon size={12} /> {busy ? "…" : p.cover_image ? "Replace cover" : "Upload cover"}
            </button>
          </>
        )}
      </div>
      <div className="p-5">
        <p className="label-overline">{p.city}, {p.state}</p>
        <h3 className="font-display text-xl mt-1">{p.name}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.address}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><BuildingsIcon size={14} /> {p.total_rooms} rooms</span>
          {p.phone && <span className="flex items-center gap-1"><PhoneIcon size={12} /> {p.phone}</span>}
          {p.email && <span className="flex items-center gap-1"><EnvelopeSimpleIcon size={12} /> {p.email}</span>}
          <span className="text-muted-foreground" data-testid={`property-times-${p.id}`}>
            Check-in {p.check_in_time || "14:00"} · Check-out {p.check_out_time || "11:00"}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-5 pt-4 border-t border-border">
          <Button
            size="sm"
            variant="default"
            className="flex-1"
            onClick={onOpenDashboard}
            data-testid={`property-open-dashboard-${p.id}`}
          >
            <ArrowSquareOutIcon size={14} className="mr-1" /> Open dashboard
          </Button>
          <Button size="icon" variant="outline" onClick={onShowQr} title="Public booking QR & link" data-testid={`property-qr-${p.id}`}>
            <QrCodeIcon size={14} />
          </Button>
          {canManage && (
            <Button size="icon" variant="outline" onClick={onEdit} title="Edit property" data-testid={`property-edit-${p.id}`}>
              <PencilSimpleIcon size={14} />
            </Button>
          )}
          {canDelete && (
            <Button size="icon" variant="outline" onClick={onDelete} title="Delete property" className="text-destructive hover:text-destructive" data-testid={`property-delete-${p.id}`}>
              <TrashIcon size={14} />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
